/**
 * Manually derived from: https://shopify.dev/docs/api/admin-graphql/2023-04/objects/CompanyLocation
 * API Version: 2022-10
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCatalogKey,
  ShopifyCatalogToCompanyLocationKey,
  ShopifyCompanyAddressKey,
  ShopifyCompanyBillingAddressToCompanyLocationKey,
  ShopifyCompanyContactRoleAssignmentKey,
  ShopifyCompanyContactRoleAssignmentToCompanyLocationKey,
  ShopifyCompanyKey,
  ShopifyCompanyLocationCatalogKey,
  ShopifyCompanyLocationCatalogToCatalogKey,
  ShopifyCompanyLocationCatalogToCompanyLocationKey,
  ShopifyCompanyLocationKey,
  ShopifyCompanyLocationToCatalogKey,
  ShopifyCompanyLocationToCompanyKey,
  ShopifyCompanyLocationToDraftOrdersKey,
  ShopifyCompanyLocationToMarketKey,
  ShopifyCompanyLocationToOrdersKey,
  ShopifyCompanyLocationToRoleAssignmentsKey,
  ShopifyCompanyLocationToShopKey,
  ShopifyCompanyLocationToStoreCreditAccountsKey,
  ShopifyCompanyShippingAddressToCompanyLocationKey,
  ShopifyDraftOrderKey,
  ShopifyDraftOrderToCompanyLocationKey,
  ShopifyMarketKey,
  ShopifyOrderKey,
  ShopifyOrderToCompanyLocationKey,
  ShopifyShopKey,
  ShopifyStoreCreditAccountKey,
  ShopifyStoreCreditAccountToCompanyLocationKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const externalIdKey = "ModelField-DataModel-Shopify-CompanyLocation-externalId";
const externalIdStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-CompanyLocation-externalId-initial";
const buyerExperienceConfigurationKey = "ModelField-DataModel-Shopify-CompanyLocation-buyerExperienceConfiguration";
const buyerExperienceConfigurationStorageEpochKey =
  "FieldStorageEpoch-DataModel-Shopify-CompanyLocation-buyerExperienceConfiguration-initial";
const taxExemptionsKey = "ModelField-DataModel-Shopify-CompanyLocation-taxExemptions";
const taxExemptionsStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-CompanyLocation-taxExemptions-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCompanyLocationKey,
  "2022-10": {
    name: "Shopify Company Location",
    graphQLTypename: "CompanyLocation",
    fields: [
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt", shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "updatedAt", shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Billing Address",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingAddress",
            graphQLSelection: `
            id
            companyName
            address1
            address2
            city
            province
            zip
            zoneCode
            country
            countryCode
            phone
            formattedAddress
            formattedArea
            recipient
            `,
          },
          availableInWebhook: { "company_locations/create": "billing_address", "company_locations/update": "billing_address" },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyAddressKey,
          inverseFieldKey: ShopifyCompanyBillingAddressToCompanyLocationKey,
        },
      },
      {
        name: "Buyer Experience Configuration",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "buyerExperienceConfiguration",
            graphQLSelection: `
            checkoutToDraft
            editableShippingAddress
            payNowOnly
            paymentTermsTemplate {
              description
              dueInDays
              id
              name
              paymentTermsType
              translatedName
            }`,
          },
        },
      },
      {
        name: "Company",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToCompanyKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "company", graphQLSelection: "id" } },
        configuration: {
          relatedModelKey: ShopifyCompanyKey,
        },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "currency", isShopifyEnum: true } },
      },
      {
        name: "External ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "externalId" } },
      },
      {
        name: "Timeline Comment",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "hasTimelineComment" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Locale",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locale", shopifyFieldKey: "locale" } },
      },
      {
        name: "Market",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToMarketKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "market", graphQLSelection: "id", shopifyFieldKey: "market" },
        },
        configuration: {
          relatedModelKey: ShopifyMarketKey,
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name", shopifyFieldKey: "name" } },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "note", shopifyFieldKey: "note" } },
      },
      {
        name: "Order Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderCount" } },
        configuration: { decimals: 0 },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "phone", shopifyFieldKey: "phone" } },
      },
      {
        name: "Shipping Address",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "shippingAddress",
            graphQLSelection: `
            id
            companyName
            address1
            address2
            city
            province
            zip
            zoneCode
            country
            countryCode
            phone
            formattedAddress
            formattedArea
            recipient
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyAddressKey,
          inverseFieldKey: ShopifyCompanyShippingAddressToCompanyLocationKey,
        },
      },
      {
        name: "Tax Exemptions",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "taxExemptions" } },
        configuration: {
          allowOther: true,
          options: [{ name: "CA_BC_CONTRACTOR_EXEMPTION" }, { name: "CA_BC_RESELLER_EXEMPTION" }],
        },
      },
      {
        name: "Tax Registration ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "taxRegistrationId" } },
      },
      {
        name: "Total Spent",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalSpent",
            graphQLSelection: `
            amount
            currencyCode`,
          },
        },
      },
      {
        name: "Draft Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyLocationToDraftOrdersKey,
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
          inverseFieldKey: ShopifyDraftOrderToCompanyLocationKey,
        },
      },
      // {
      //   name: "Events",
      //   fieldType: FieldType.HasMany,
      //   configuration: {
      //     dependent: "delete",
      //     relatedModelKey: ShopifyEventKey,
      //     inverseFieldKey: ShopifyEventToCompanyLocationKey,
      //   },
      // },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyLocationToOrdersKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToCompanyLocationKey,
        },
      },
      {
        name: "Role Assignments",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyLocationToRoleAssignmentsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "role_assignments",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyContactRoleAssignmentKey,
          inverseFieldKey: ShopifyCompanyContactRoleAssignmentToCompanyLocationKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "companyLocations",
      filters: {
        updatedAt: { sortKey: "UPDATED_AT" },
        createdAt: { sortKey: "CREATED_AT" },
      },
      sortableBy: {
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
        createdAt: { sortKey: "CREATED_AT", reverse: true },
        companyAndLocationName: { sortKey: "COMPANY_AND_LOCATION_NAME", reverse: false },
        companyId: { sortKey: "COMPANY_ID", reverse: false },
        id: { sortKey: "ID", reverse: false },
        name: { sortKey: "NAME", reverse: false },
        relevance: { sortKey: "RELEVANCE", reverse: false },
      },
    },
  },
  "2023-01": {
    hasMetafields: true,
  },

  "2024-04": {
    fields: [
      {
        name: "Order Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderCount" } },
        isDeleted: true,
      },
      {
        name: "Orders Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "ordersCount",
            graphQLSelection: `
        count
        precision`,
          },
        },
      },
      {
        name: "Catalogs",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCompanyLocationToCatalogKey,
        configuration: {
          joinModelKey: ShopifyCompanyLocationCatalogKey,
          relatedModelKey: ShopifyCatalogKey,
          inverseJoinModelFieldKey: ShopifyCompanyLocationCatalogToCompanyLocationKey,
          inverseRelatedModelFieldKey: ShopifyCompanyLocationCatalogToCatalogKey,
          inverseFieldKey: ShopifyCatalogToCompanyLocationKey,
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Shipping Address",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "shippingAddress",
            shopifyFieldKey: "shipping_address",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
            graphQLSelection: `
            id
            companyName
            address1
            address2
            city
            province
            zip
            zoneCode
            country
            countryCode
            phone
            formattedAddress
            formattedArea
            recipient
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyAddressKey,
          inverseFieldKey: ShopifyCompanyShippingAddressToCompanyLocationKey,
        },
      },
      {
        name: "Tax Exemptions",
        key: taxExemptionsKey,
        initialStorageEpochKey: taxExemptionsStorageEpochKey,
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "taxExemptions",
            shopifyFieldKey: "tax_exemptions",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
        configuration: {
          allowOther: true,
          options: [{ name: "CA_BC_CONTRACTOR_EXEMPTION" }, { name: "CA_BC_RESELLER_EXEMPTION" }],
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "updatedAt",
            shopifyFieldKey: "updated_at",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "createdAt",
            shopifyFieldKey: "created_at",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
      {
        name: "Billing Address",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingAddress",
            shopifyFieldKey: "billing_address",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
            graphQLSelection: `
            id
            companyName
            address1
            address2
            city
            province
            zip
            zoneCode
            country
            countryCode
            phone
            formattedAddress
            formattedArea
            recipient
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyAddressKey,
          inverseFieldKey: ShopifyCompanyBillingAddressToCompanyLocationKey,
        },
      },
      {
        name: "Buyer Experience Configuration",
        key: buyerExperienceConfigurationKey,
        initialStorageEpochKey: buyerExperienceConfigurationStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "buyerExperienceConfiguration",
            shopifyFieldKey: "buyer_experience_configuration",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
            graphQLSelection: `
            checkoutToDraft
            editableShippingAddress
            payNowOnly
            paymentTermsTemplate {
              description
              dueInDays
              id
              name
              paymentTermsType
              translatedName
            }`,
          },
        },
      },
      {
        name: "Company",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToCompanyKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "company",
            shopifyFieldKey: "company",
            graphQLSelection: "id",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyKey,
        },
      },
      {
        name: "External ID",
        key: externalIdKey,
        initialStorageEpochKey: externalIdStorageEpochKey,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "externalId",
            shopifyFieldKey: "external_id",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
      {
        name: "Locale",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "locale",
            shopifyFieldKey: "locale",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "name",
            shopifyFieldKey: "name",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "note",
            shopifyFieldKey: "note",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "phone",
            shopifyFieldKey: "phone",
            availableInWebhook: ["company_locations/create", "company_locations/update"],
          },
        },
      },
    ],
  },
  "2025-10": {
    fields: [
      {
        name: "Company Location Store Credit Accounts",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyLocationToStoreCreditAccountsKey,
        configuration: {
          relatedModelKey: ShopifyStoreCreditAccountKey,
          inverseFieldKey: ShopifyStoreCreditAccountToCompanyLocationKey,
        },
      },
    ],
  },
};

export const ShopifyCompanyLocation = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
